import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Phone, CheckCircle, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../mock';

const RatgeberNachUnfallPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Nach Unfall: Checkliste & Was tun? | Ratgeber 2026</title>
        <meta name="description" content="Was tun nach Autounfall? Komplette Checkliste: Unfallstelle sichern, Polizei rufen, Daten austauschen, Fotos machen, Gutachter beauftragen. Schritt-für-Schritt Anleitung." />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/ratgeber/nach-unfall-checkliste" />
        
        {/* HowTo Schema für KI */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Was tun nach einem Autounfall",
            "description": "Schritt-für-Schritt Anleitung für richtiges Verhalten nach Verkehrsunfall",
            "totalTime": "PT15M",
            "step": [
              {
                "@type": "HowToStep",
                "name": "Unfallstelle sichern",
                "text": "Warnblinker einschalten, Warnweste anziehen, Warndreieck aufstellen (50m Landstraße, 100m Autobahn)",
                "position": 1
              },
              {
                "@type": "HowToStep",
                "name": "Polizei informieren",
                "text": "Bei Personenschaden, unklarer Schuldfrage oder Schaden über 1.000€ Polizei rufen",
                "position": 2
              },
              {
                "@type": "HowToStep",
                "name": "Daten austauschen",
                "text": "Name, Adresse, Telefon, Kennzeichen, Versicherungsdaten vom Unfallgegner notieren",
                "position": 3
              },
              {
                "@type": "HowToStep",
                "name": "Unfallstelle fotografieren",
                "text": "Viele Fotos aus verschiedenen Winkeln: Gesamtsituation, beide Fahrzeuge, alle Schäden",
                "position": 4
              },
              {
                "@type": "HowToStep",
                "name": "Versicherung informieren",
                "text": "Eigene Versicherung innerhalb 1 Woche informieren, auch wenn nicht schuld",
                "position": 5
              },
              {
                "@type": "HowToStep",
                "name": "Gutachter beauftragen",
                "text": "Ab 750€ Schaden unabhängiges Gutachten beauftragen. Kosten zahlt gegnerische Versicherung.",
                "position": 6
              }
            ]
          })}
        </script>
        
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Nach dem Unfall: Checkliste und Was Sie tun sollten",
            "author": {
              "@type": "Organization", 
              "name": "Dein Gutachten Direkt",
              "url": "https://www.dein-gutachten-direkt.de"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Dein Gutachten Direkt",
              "logo": {
                "@type": "ImageObject",
                "url": "https://customer-assets.emergentagent.com/job_gutachten-preview/artifacts/gusc9edr_image.png",
                "width": 200,
                "height": 60,
                "caption": "Dein Gutachten Direkt Logo"
              }
            },
            "image": {
              "@type": "ImageObject",
              "url": "https://customer-assets.emergentagent.com/job_gutachten-preview/artifacts/gusc9edr_image.png",
              "width": 1200,
              "height": 630,
              "caption": "Nach dem Unfall Checkliste",
              "creditText": "Dein Gutachten Direkt",
              "creator": {
                "@type": "Organization",
                "name": "Dein Gutachten Direkt"
              },
              "copyrightNotice": "© 2026 Dein Gutachten Direkt"
            },
            "datePublished": "2026-01-15",
            "dateModified": "2026-02-03",
            "description": "Komplette Anleitung was nach einem Autounfall zu tun ist"
          })}
        </script>
      </Helmet>
      <Header />
      <main className="pt-24 pb-16">
        <article className="max-w-4xl mx-auto px-6">
          {/* Author & Date Info */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <span className="text-brand-green font-semibold text-sm">Ratgeber</span>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-brand-green/10 rounded-full flex items-center justify-center">
                  <Award className="w-4 h-4 text-brand-green" />
                </div>
                <span>Geschrieben von: <strong>Dein Gutachten Direkt Team</strong></span>
              </div>
              <span>•</span>
              <span>Veröffentlicht: <strong>15. Januar 2026</strong></span>
              <span>•</span>
              <span>Zuletzt aktualisiert: <strong>3. Februar 2026</strong></span>
            </div>
            <p className="text-xs text-gray-500 mt-2">Von erfahrenen Kfz-Sachverständigen</p>
          </div>
          
          <div className="mb-8">
            <span className="text-brand-green font-semibold text-sm">Ratgeber</span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-4">Nach dem Unfall: Was jetzt zu tun ist</h1>
            <p className="text-xl text-gray-600">Schritt-für-Schritt Checkliste für die ersten Minuten und Tage nach einem Autounfall</p>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Sofortmaßnahmen an der Unfallstelle</h2>
            
            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6">
              <h3 className="font-bold text-red-900 mb-3">1. Unfallstelle sichern (Erste 60 Sekunden)</h3>
              <ul className="space-y-2 text-gray-800">
                <li>✓ Warnblinker einschalten</li>
                <li>✓ Warnweste anziehen (Pflicht!)</li>
                <li>✓ Warndreieck aufstellen (50m Landstraße, 100m Autobahn)</li>
                <li>✓ Unfallstelle absichern, Verletzte versorgen</li>
              </ul>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6">
              <h3 className="font-bold text-blue-900 mb-3">2. Polizei informieren (Bei Personenschaden PFLICHT)</h3>
              <p className="text-gray-800 mb-2">Rufen Sie die Polizei wenn:</p>
              <ul className="space-y-2 text-gray-800">
                <li>✓ Personen verletzt sind (auch leicht)</li>
                <li>✓ Schuldfrage unklar ist</li>
                <li>✓ Gegner keine Daten herausrückt</li>
                <li>✓ Schaden über 1.000 € (empfohlen)</li>
                <li>✓ Ausländisches Kennzeichen beteiligt</li>
              </ul>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. Daten mit Unfallgegner austauschen</h3>
            <p className="text-gray-700 mb-4">Notieren Sie ALLE folgenden Daten vom Unfallgegner:</p>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li>✓ Name, Vorname, Adresse</li>
              <li>✓ Telefonnummer</li>
              <li>✓ Kennzeichen</li>
              <li>✓ Versicherung (Name + Versicherungsnummer)</li>
              <li>✓ Fahrzeugdaten (Marke, Modell, Farbe)</li>
              <li>✓ Namen von Zeugen (falls vorhanden)</li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">4. Unfallstelle fotografieren</h3>
            <p className="text-gray-700 mb-4">Machen Sie viele Fotos aus verschiedenen Winkeln:</p>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li>✓ Gesamtsituation (Übersichtsfotos)</li>
              <li>✓ Position beider Fahrzeuge</li>
              <li>✓ Alle Schäden (nah + weit)</li>
              <li>✓ Bremsspuren, Glassplitter, Trümmerteile</li>
              <li>✓ Straßenschilder, Ampeln (für Orientierung)</li>
              <li>✓ Kennzeichen beider Fahrzeuge</li>
            </ul>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-6">
              <h3 className="font-bold text-yellow-900 mb-2">⚠️ WICHTIG: Nichts unterschreiben!</h3>
              <p className="text-gray-800">Unterschreiben Sie KEINE Erklärungen des Unfallgegners oder dessen Versicherung ohne Beratung. Kontaktieren Sie erst Ihre Versicherung oder einen Anwalt.</p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Nach der Unfallstelle: Nächste 24 Stunden</h2>
            
            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">5. Eigene Versicherung informieren</h3>
            <p className="text-gray-700 mb-4">Melden Sie den Schaden IMMER Ihrer eigenen Versicherung – auch wenn Sie nicht schuld sind. Frist: meist 1 Woche, besser sofort.</p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">6. Gutachter beauftragen (ab 750 € Schaden)</h3>
            <p className="text-gray-700 mb-4">Bei Schäden über 750 € ist ein unabhängiges Gutachten empfehlenswert:</p>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li>✓ Sichert alle Ansprüche (Reparatur, Wertminderung, Nutzungsausfall)</li>
              <li>✓ Sie haben freie Gutachterwahl (nicht die Versicherung!)</li>
              <li>✓ Kosten zahlt gegnerische Versicherung</li>
              <li>✓ Rechtlich belastbar bei Streit</li>
            </ul>

            <div className="bg-brand-green/10 border-2 border-brand-green/30 rounded-2xl p-8 my-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Jetzt Gutachten beauftragen</h3>
              <p className="text-gray-700 mb-6">Wir kommen in 30 Minuten zu Ihnen – Hamburg & 50 km Umgebung</p>
              <a href={`tel:${COMPANY_INFO.phone}`}>
                <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-xl font-bold">
                  <Phone className="w-5 h-5 mr-3" />
                  {COMPANY_INFO.phone}
                </Button>
              </a>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-3">7. Fahrzeug NICHT reparieren lassen</h3>
            <p className="text-gray-700 mb-4">Wichtig: Reparieren Sie das Fahrzeug NICHT vor der Begutachtung! Der Gutachter muss den Originalzustand sehen.</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Häufige Fehler vermeiden</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="font-bold text-gray-900 mb-2">❌ Fehler 1: Keine Fotos gemacht</h4>
                <p className="text-gray-700 text-sm">Machen Sie IMMER Fotos – auch bei kleinen Schäden. Später kann man nichts mehr nachweisen.</p>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="font-bold text-gray-900 mb-2">❌ Fehler 2: Nur Kostenvoranschlag statt Gutachten</h4>
                <p className="text-gray-700 text-sm">Bei Schäden über 750 € verlieren Sie mit Kostenvoranschlag oft Geld (Wertminderung, Nutzungsausfall fehlen).</p>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="font-bold text-gray-900 mb-2">❌ Fehler 3: Zu spät Gutachter beauftragt</h4>
                <p className="text-gray-700 text-sm">Je früher, desto besser. Idealerweise innerhalb 24-48 Stunden nach Unfall.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Zusammenfassung: Checkliste</h3>
              <div className="text-left space-y-2">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-brand-green mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800">Unfallstelle sichern (Warndreieck, Warnweste)</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-brand-green mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800">Polizei rufen (bei Personenschaden oder Unklarheiten)</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-brand-green mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800">Daten austauschen (Name, Versicherung, Kennzeichen)</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-brand-green mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800">Fotos machen (viele, aus allen Winkeln)</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-brand-green mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800">Versicherung informieren (innerhalb 1 Woche)</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-brand-green mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800">Gutachter beauftragen (ab 750 € Schaden)</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-brand-green mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800">NICHT reparieren vor Begutachtung</span>
                </div>
              </div>
            </div>

            {/* Internal Links Section */}
            <div className="bg-white rounded-2xl p-8 mt-8 border-2 border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Weitere hilfreiche Ratgeber</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/ratgeber/wertminderung" className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-brand-green hover:shadow-md transition-all group">
                  <ArrowRight className="w-5 h-5 text-brand-green group-hover:translate-x-1 transition-transform" />
                  <div>
                    <span className="font-semibold text-gray-900 group-hover:text-brand-green transition-colors">Wertminderung nach Unfall</span>
                    <p className="text-sm text-gray-600">Anspruch & Berechnung erklärt</p>
                  </div>
                </Link>
                <Link to="/ratgeber/gutachten-oder-kostenvoranschlag" className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-brand-green hover:shadow-md transition-all group">
                  <ArrowRight className="w-5 h-5 text-brand-green group-hover:translate-x-1 transition-transform" />
                  <div>
                    <span className="font-semibold text-gray-900 group-hover:text-brand-green transition-colors">Gutachten vs. Kostenvoranschlag</span>
                    <p className="text-sm text-gray-600">Was ist der Unterschied?</p>
                  </div>
                </Link>
              </div>
              
              <h4 className="font-bold text-gray-900 mt-8 mb-4">Gutachten in Ihrer Region</h4>
              <div className="flex flex-wrap gap-3">
                <Link to="/unfallgutachten-hamburg" className="text-sm px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Hamburg</Link>
                <Link to="/unfallgutachten-norderstedt" className="text-sm px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Norderstedt</Link>
                <Link to="/unfallgutachten-pinneberg" className="text-sm px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Pinneberg</Link>
                <Link to="/unfallgutachten-mainz" className="text-sm px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Mainz</Link>
                <Link to="/unfallgutachten-frankfurt" className="text-sm px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Frankfurt</Link>
                <Link to="/unfallgutachten-wiesbaden" className="text-sm px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Wiesbaden</Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default RatgeberNachUnfallPage;